import { IconNames } from '@blueprintjs/icons';
import { capitalizeString, DateFormats, formatDate } from '@strata/helper-functions';
import { GridWithFilters, PrimaryData, SecondaryData } from '@strata/react-grid';
import { formatRateString } from '@strata/react-servicing-rate';
import { Icon } from '@strata/react-status-icons';
import { StatusIndicator } from '@strata/react-status-indicators';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ListPageHeader } from 'components/ListPageHeader';
import { DeleteRateRuleDialog } from 'pages/rateRules/dialogs/DeleteRateRuleDialog';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { get, useGet } from 'services/Http';

const RateRuleStatuses = {
    ACTIVE: 'ACTIVE',
    INACTIVE: 'INACTIVE',
};

const mapIsGenericToProductType = (isGeneric) => {
    if (isGeneric === undefined) {
        return undefined;
    }
    return isGeneric ? 'Generic' : 'Brand';
};

const fetchRateRules = async () => {
    try {
        const [rateRules, products] = await Promise.all([
            get('/ratesetter/admin/rate_rules/all'),
            get('/reference/productservices/summaryonly'),
        ]);

        return (rateRules ?? []).map((rr) => ({
            ...rr,
            product:
                products?.find((p) => p.oid === rr.product_service_oid)?.description ??
                rr.product_service_oid,
            productType: mapIsGenericToProductType(rr.is_generic),
            benefit: capitalizeString(rr.benefit),
            placeOfService: rr.place_of_service ?? 'All',
            status: dayjs().isBefore(rr.effective_start_date)
                ? RateRuleStatuses.INACTIVE
                : RateRuleStatuses.ACTIVE,
        }));
    } catch (err) {
        handleError('Error fetching rate rules', err);
        return undefined;
    }
};

const columns = ({ navigate, setRuleToDelete }) => [
    {
        header: 'Product',
        accessorKey: 'product',
        size: 100,
        cell: ({ getValue }) => <PrimaryData data={getValue()} />,
    },
    {
        header: 'Payer Segment',
        accessorKey: 'segment_id',
        size: 80,
    },
    {
        header: 'Product Type',
        accessorKey: 'productType',
        size: 65,
    },
    {
        header: 'Benefit',
        accessorKey: 'benefit',
        size: 75,
    },
    {
        header: 'POS',
        accessorKey: 'placeOfService',
        size: 70,
    },
    {
        header: 'Referent',
        accessorKey: 'referent_price_type',
        size: 58,
    },
    {
        header: 'Default Rate',
        accessorKey: 'default_rate',
        size: 60,
        cell: ({ getValue }) => (
            <SecondaryData data={formatRateString({ bidDecimal: getValue() })} />
        ),
    },
    {
        header: 'Baseline Rate',
        accessorKey: 'baseline_rate',
        size: 60,
        cell: ({ getValue }) => (
            <SecondaryData data={formatRateString({ bidDecimal: getValue() })} />
        ),
    },
    {
        header: 'Market Override Rate',
        accessorKey: 'market_override_rate',
        size: 60,
        cell: ({ getValue }) => (
            <SecondaryData data={formatRateString({ bidDecimal: getValue() ?? undefined })} />
        ),
    },
    {
        header: 'Effective Start Date',
        accessorKey: 'effective_start_date',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        header: 'Modified On',
        accessorKey: 'modified_at',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        header: 'Last Modified By',
        accessorKey: 'upn',
        size: 80,
    },
    {
        header: 'Status',
        accessorKey: 'status',
        size: 94,
        cell: ({ getValue }) => (
            <StatusIndicator
                status={
                    getValue() === RateRuleStatuses.ACTIVE
                        ? StatusIndicator.Status.SUCCESS
                        : StatusIndicator.Status.DEFAULT
                }
                message={getValue()}
            />
        ),
    },
    {
        header: 'Actions',
        accessorKey: 'actions',
        size: 70,
        enableSorting: false,
        cell: ({ row }) => (
            <div className="sds_filter-list__actions">
                <Icon
                    icon={IconNames.EDIT}
                    intent={Icon.Intent.SECONDARY}
                    title="Edit"
                    onClick={() =>
                        navigate({
                            pathname: `/rate-rules/${row.original.rate_rule_id}`,
                        })
                    }
                />
                <Icon
                    icon={IconNames.TRASH}
                    intent={Icon.Intent.SECONDARY}
                    title="Delete"
                    onClick={() => setRuleToDelete(row.original)}
                />
            </div>
        ),
    },
];

export const RateRules = () => {
    const navigate = useNavigate();
    const [rateRules, setRateRules] = useState();
    useEffect(() => {
        void (async () => {
            setRateRules(await fetchRateRules());
        })();
    }, []);

    const payers = useGet('/reference/payers/');
    const payerMap = useMemo(
        () =>
            payers?.reduce(
                (map, payer) => ({
                    ...map,
                    [payer.payer_id]: `${payer.payer_name} - ${payer.payer_id}`,
                }),
                {},
            ),
        [payers],
    );
    const payerSegments = useGet('/reference/payer_segments');
    const payerSegmentMap = useMemo(
        () =>
            payerSegments?.reduce(
                (map, segment) => ({
                    ...map,
                    [segment.segment_id]: `${segment.segment_name} - ${segment.segment_id}`,
                }),
                {},
            ),
        [payerSegments],
    );

    const filterCategories = [
        {
            key: 'product',
            title: 'Products',
        },
        {
            key: 'benefit',
            title: 'Benefit',
        },
        {
            key: 'productType',
            title: 'Product Type',
        },
        {
            key: 'referent_price_type',
            title: 'Referent',
        },
        {
            key: 'payer_id',
            title: 'Payer',
            valueDisplayTextMap: payerMap,
            showCounts: true,
        },
        {
            key: 'segment_id',
            title: 'Payer Segment',
            valueDisplayTextMap: payerSegmentMap,
            showCounts: true,
        },
        {
            key: 'location',
            title: 'Location',
        },
        {
            key: 'line_of_business',
            title: 'Line of Business',
        },
    ];

    const [ruleToDelete, setRuleToDelete] = useState();

    return (
        <div className="sds_filter-list">
            <DeleteRateRuleDialog
                ruleToDelete={ruleToDelete}
                onClose={() => setRuleToDelete(undefined)}
                onDeleteSuccess={async () => {
                    setRateRules();
                    setRateRules(await fetchRateRules());
                }}
            />
            <GridWithFilters
                headerContent={
                    <ListPageHeader
                        pageTitle="Rate Rules"
                        buttons={[
                            { label: 'Add Rate Rule', path: `/rate-rules/${CREATE_ROUTE}` },
                            { label: 'Bulk Upload Rate Rules', path: '/rate-rules/bulk-upload' },
                        ]}
                    />
                }
                enableSearch
                filterCategories={filterCategories}
                sortProperties={[{ id: 'product', desc: false }]}
                isLoading={!rateRules}
                data={rateRules}
                columns={columns({ navigate, setRuleToDelete })}
            />
        </div>
    );
};
